/** Reports Information Architecture 1.0 — view routing helpers */

export type ReportsView = 'overview' | 'findings' | 'assessments' | 'uploads'

export const REPORTS_VIEWS: ReportsView[] = ['overview', 'findings', 'assessments', 'uploads']

export function isReportsView(value: string | null | undefined): value is ReportsView {
  return REPORTS_VIEWS.includes(String(value || '') as ReportsView)
}

/**
 * Resolve durable view from URL, including legacy redirects:
 * ?tab=uploaded → overview (or findings if section=filtered-vulnerabilities)
 * ?tab=scanner → assessments
 * ?scanId= → assessments (parsed deep-link)
 */
export function resolveReportsView(searchParams: URLSearchParams): ReportsView {
  const explicit = searchParams.get('view')
  if (isReportsView(explicit)) return explicit

  const tab = searchParams.get('tab')
  const scanId = searchParams.get('scanId') || searchParams.get('scannerScanId')
  const section = searchParams.get('section')

  if (scanId || tab === 'scanner') return 'assessments'
  if (
    section === 'filtered-vulnerabilities' ||
    searchParams.get('severity') ||
    searchParams.get('state') ||
    searchParams.get('asset') ||
    searchParams.get('assetId')
  ) {
    return 'findings'
  }
  if (tab === 'uploaded') return 'overview'
  return 'overview'
}

/** Build path that preserves filters when switching views. */
export function buildReportsViewHref(
  view: ReportsView,
  current: URLSearchParams,
  extra: Record<string, string | null | undefined> = {},
): string {
  const next = new URLSearchParams()
  next.set('view', view)

  // Preserve org-scoped deep-link filters where relevant
  if (view === 'findings') {
    for (const key of ['state', 'severity', 'scanner', 'date', 'asset', 'assetId', 'informationSystemId', 'search']) {
      const v = extra[key] ?? current.get(key)
      if (v) next.set(key, v)
    }
  }
  if (view === 'assessments') {
    for (const key of ['scanId', 'scannerScanId', 'assetId', 'provider', 'status', 'target']) {
      const v = extra[key] ?? current.get(key)
      if (v) next.set(key, v)
    }
  }
  if (view === 'uploads') {
    const v = extra.search ?? current.get('search')
    if (v) next.set('search', v)
  }

  const qs = next.toString()
  return qs ? `/reports?${qs}` : '/reports?view=overview'
}
